import { Button, FormControl, FormLabel, Input, Stack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { DraftAgent } from '@blockpulse3/graphql/hooks';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import {
  draftAgentDefaultValues,
  schemaAgent,
} from '../../OnboardingSPV/OnboardingSPVWizard/provider/schema';

type Props = {
  defaultValues: DraftAgent;
  onCancel?: () => void;
  onSubmit: (data: DraftAgent) => void;
};

/**
 * NaturalOtherRoleForm.
 * Natural version of the OtherRoleForm. Uses a forwardRef function to be able to submit it
 * from the outside.
 */
export const NaturalOtherRoleForm = React.forwardRef<HTMLFormElement, Props>((props, ref) => {
  const { defaultValues = draftAgentDefaultValues, onCancel = noop, onSubmit } = props;

  const i18nGeneral = useTranslations('General');
  const i18nNaturalOtherRoleForm = useTranslations('NaturalOtherRoleForm');

  const { register, formState, setValue, handleSubmit } = useForm<DraftAgent>({
    defaultValues,
    resolver: yupResolver(schemaAgent),
  });

  const handleFormSubmit = (data: DraftAgent): void => {
    onSubmit(data);
  };

  const handleFormCancel = (): void => {
    onCancel();
  };

  useEffect(() => {
    /* ** Fill fields on default values change: switching the it's me switch ** */
    setValue('firstName', defaultValues.firstName);
    setValue('lastName', defaultValues.lastName);
  }, [defaultValues, setValue]);

  return (
    <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing="8">
        <Stack spacing="4">
          <FormControl isInvalid={!!formState.errors?.email}>
            <FormLabel htmlFor="email">{i18nNaturalOtherRoleForm('EmailField')}</FormLabel>
            <Input id="email" type="text" {...register('email')} />
            <ErrorMessage error={formState.errors?.email} />
          </FormControl>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors?.firstName}>
              <FormLabel htmlFor="firstName">
                {i18nNaturalOtherRoleForm('FirstNameField')}
              </FormLabel>
              <Input id="firstName" type="text" {...register('firstName')} />
              <ErrorMessage error={formState.errors?.firstName} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors?.lastName}>
              <FormLabel htmlFor="lastName">{i18nNaturalOtherRoleForm('LastNameField')}</FormLabel>
              <Input id="name" type="text" {...register('lastName')} />
              <ErrorMessage error={formState.errors?.lastName} />
            </FormControl>
          </Stack>
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
          <Button type="button" variant="secondary" w="full" onClick={handleFormCancel}>
            {i18nGeneral('Delete')}
          </Button>
          <Button type="submit" w="full">
            {i18nGeneral('Submit')}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
});

NaturalOtherRoleForm.displayName = 'NaturalOtherRoleForm';

export type NaturalOtherRoleFormProps = Props;
